/**
 * SEARCH-002 §4.1 — Regroupement des résultats de recherche par type.
 *
 * SOURCE UNIQUE partagée par la palette ⌘K (`useSearchPaletteData`) et le
 * sélecteur « Fiche liée » d'une tâche (`LinkedEntityPicker`) : les deux
 * surfaces affichent la MÊME réponse serveur, elles doivent donc l'ordonner de
 * la même façon.
 *
 * ⚠️ L'ordre des GROUPES suit la PERTINENCE renvoyée par le serveur, pas l'ordre
 * du registre. `GET /api/search/global` trie déjà ses lignes par
 * `tier DESC, sim DESC` (cf. `global_search.py`) : le premier hit d'un type est
 * donc le meilleur score de ce type, et l'ordre d'apparition des types dans la
 * réponse EST le classement de pertinence. Retrier par
 * `CATEGORY_REGISTRY[].order` jette cette information : sur « lesli »,
 * l'intervenant « Leslie DUMONTET » (correspondance de préfixe, sim 0.99) se
 * retrouvait sous trois groupes de bruit flou (sim 0.5) — hors de la zone
 * visible du `CommandList` (max-h 300 px), donc invisible pour l'utilisateur,
 * qui en conclut que le type n'est pas proposé.
 *
 * ⚠️ Le DÉPARTAGE à pertinence égale (même palier, même similarité) se fait
 * CÔTÉ SERVEUR, dans le tri SQL de `global_search.py`, par la position du type
 * dans `SOURCE_TYPES` (`registry.py`) — liste rangée dans l'ordre de
 * `CATEGORY_REGISTRY[].order` : à égalité, le client passe devant un homonyme
 * d'une autre catégorie. Ici, l'ordre d'apparition est repris tel quel.
 * La comparaison par `order` du tri ci-dessous ne s'exerce jamais sur une
 * réponse serveur : deux types distincts n'ont jamais le même premier index.
 * Un départage fait ICI arriverait trop tard : l'ordre des lignes, donc celui
 * des groupes, est fixé par le serveur.
 */

import { CATEGORY_REGISTRY, type SourceType } from '@/lib/searchCategories'

/** Un groupe affichable : le type et ses hits, dans l'ordre de renvoi serveur. */
export interface SearchHitGroup<T> {
    type: SourceType
    hits: T[]
}

/**
 * Regroupe des hits par `type` en préservant la pertinence serveur.
 *
 * @param hits   Hits dans l'ORDRE DE RENVOI du serveur (ne pas pré-trier).
 * @param skip   Prédicat optionnel : hit à exclure des groupes (la palette y
 *               retire le « meilleur résultat », déjà affiché en tête).
 */
export function groupHitsByRelevance<T extends { type: SourceType }>(
    hits: readonly T[],
    skip?: (hit: T) => boolean,
): SearchHitGroup<T>[] {
    const byType = new Map<SourceType, T[]>()
    const firstIndex = new Map<SourceType, number>()
    hits.forEach((h, i) => {
        if (skip?.(h)) return
        if (!byType.has(h.type)) {
            byType.set(h.type, [])
            firstIndex.set(h.type, i)
        }
        byType.get(h.type)!.push(h)
    })
    return [...byType.keys()]
        .sort((a, b) => {
            const d = firstIndex.get(a)! - firstIndex.get(b)!
            return d !== 0 ? d : CATEGORY_REGISTRY[a].order - CATEGORY_REGISTRY[b].order
        })
        .map((type) => ({ type, hits: byType.get(type)! }))
}
